import React, { useState, useCallback, useMemo, memo } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Avatar,
   Button,
   Chip,
   Stack,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogContentText,
   DialogActions,
   Tooltip,
   Fade,
   CircularProgress,
   alpha,
} from '@mui/material';
import {
   Cancel as CancelIcon,
   AccessTime as AccessTimeIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   OpenInNew as OpenInNewIcon,
   Refresh as RefreshIcon,
   CheckCircle as CheckCircleIcon,
   Schedule as ScheduleIcon,
   Warning as WarningIcon,
   Mail as MailIcon,
   People as PeopleIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../../utils/auth';
import { formatDistanceToNow, differenceInDays, isPast } from 'date-fns';

// Interface for each recipient in the batch
export interface InvitationRecipient {
   recipientEmail: string;
   recipientName: string;
   recipientPhoto?: string;
   status: 'pending' | 'accepted' | 'declined';
   role: 'viewer' | 'editor' | 'owner';
   invitationId?: string;
}

// Interface for the batch invitation data (sent by owner)
export interface AlbumInvitationSent {
   id: string; // Feed card ID
   batchId?: string; // Batch ID for grouping
   albumId: string;
   albumTitle: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumIsPublic: boolean;
   recipients: InvitationRecipient[];
   sentAt: Date | any;
   expiresAt?: Date | any;
   lastResentAt?: Date | any;
   isReminder?: boolean;
}

interface AlbumInvitationSentCardProps {
   invitation: AlbumInvitationSent;
   onResendSuccess?: (batchId: string) => void;
   onCancelSuccess?: (batchId: string) => void;
   onViewAlbum?: (albumId: string) => void;
}

// Helper function to convert Firebase timestamp to Date
const toDate = (timestamp: any): Date => {
   if (!timestamp) return new Date();
   if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
   }
   if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
   }
   if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      return new Date(timestamp._seconds * 1000);
   }
   if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
   }
   if (timestamp instanceof Date) {
      return timestamp;
   }
   return new Date(timestamp);
};

// Helper function to format relative time
const formatRelativeTime = (timestamp: any): string => {
   try {
      const date = toDate(timestamp);
      if (isNaN(date.getTime())) return 'Unknown';
      return formatDistanceToNow(date, { addSuffix: true });
   } catch {
      return 'Unknown';
   }
};

// Helper function to get expiration status
const getExpirationText = (expiresAt: any): { text: string; isExpired: boolean; isUrgent: boolean } => {
   if (!expiresAt) return { text: '', isExpired: false, isUrgent: false };
   
   try {
      const expDate = toDate(expiresAt);
      if (isNaN(expDate.getTime())) return { text: '', isExpired: false, isUrgent: false };
      
      if (isPast(expDate)) {
         return { text: 'Expired', isExpired: true, isUrgent: false };
      }
      
      const daysUntilExpiry = differenceInDays(expDate, new Date());
      const isUrgent = daysUntilExpiry <= 2;
      
      if (daysUntilExpiry === 0) {
         return { text: 'Expires today', isExpired: false, isUrgent: true };
      } else if (daysUntilExpiry === 1) {
         return { text: 'Expires tomorrow', isExpired: false, isUrgent: true };
      } else {
         return { text: `Expires in ${daysUntilExpiry} days`, isExpired: false, isUrgent };
      }
   } catch {
      return { text: '', isExpired: false, isUrgent: false };
   }
};

// Status color helper
const getStatusColor = (status: string) => {
   switch (status) {
      case 'accepted':
         return { bg: '#ecfdf5', color: '#10b981', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
      case 'declined':
         return { bg: '#fef2f2', color: '#dc2626', icon: <CancelIcon sx={{ fontSize: 14 }} /> };
      case 'pending':
      default:
         return { bg: '#eff6ff', color: '#3b82f6', icon: <ScheduleIcon sx={{ fontSize: 14 }} /> };
   }
};

const AlbumInvitationSentCard: React.FC<AlbumInvitationSentCardProps> = memo(({
   invitation,
   onResendSuccess,
   onCancelSuccess,
   onViewAlbum,
}) => {
   const navigate = useNavigate();
   const [isResending, setIsResending] = useState(false);
   const [cancellingRecipient, setCancellingRecipient] = useState<string | null>(null);
   const [showCancelDialog, setShowCancelDialog] = useState(false);
   const [recipientToCancel, setRecipientToCancel] = useState<InvitationRecipient | null>(null);
   const [showResendSuccess, setShowResendSuccess] = useState(false);
   const [isVisible, setIsVisible] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [localRecipients, setLocalRecipients] = useState(invitation.recipients || []);

   // Memoize stats calculation
   const { totalCount, acceptedCount, declinedCount, pendingCount } = useMemo(() => {
      const recipients = localRecipients;
      return {
         totalCount: recipients.length,
         acceptedCount: recipients.filter(r => r.status === 'accepted').length,
         declinedCount: recipients.filter(r => r.status === 'declined').length,
         pendingCount: recipients.filter(r => r.status === 'pending').length,
      };
   }, [localRecipients]);

   const recipients = localRecipients;

   // Memoize expiration check
   const { text: expirationText, isExpired, isUrgent } = useMemo(() => 
      getExpirationText(invitation.expiresAt), [invitation.expiresAt]);

   // Handle resend - using the same API as send-participant-reminder
   // Allow resending even when expired to reactivate invitations
   const handleResend = useCallback(async () => {
      if (isResending) return;

      // Send ALL recipients to the server - the server will:
      // - Skip accepted recipients who are still members
      // - Recreate cards for accepted recipients who have been removed from the album
      // - Update existing cards for pending recipients
      // - Create new cards for declined recipients
      if (recipients.length === 0) {
         setError('No invitations to resend');
         setTimeout(() => setError(null), 3000);
         return;
      }

      setIsResending(true);
      setError(null);

      try {
         const headers = await getAuthHeaders();
         
         // Format ALL recipients for the send-participant-reminder API
         // Server will check membership status for accepted users
         const formattedRecipients = recipients.map(r => ({
            userId: null, // May not have userId for email-only invites
            email: r.recipientEmail,
            currentStatus: r.status, // Include current status for server-side logic
         }));

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/send-participant-reminder`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
            body: JSON.stringify({
               albumId: invitation.albumId,
               albumTitle: invitation.albumTitle,
               recipients: formattedRecipients,
               message: '', // Default empty message
               photos: [], // No photos for simple resend
            }),
         });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || errorData.message || 'Failed to resend invitations');
         }

         setShowResendSuccess(true);
         setTimeout(() => setShowResendSuccess(false), 3000);
         onResendSuccess?.(invitation.batchId || invitation.id);
      } catch (err) {
         console.error('Error resending invitations:', err);
         setError(err instanceof Error ? err.message : 'Failed to resend invitations');
         setTimeout(() => setError(null), 5000);
      } finally {
         setIsResending(false);
      }
   }, [invitation.batchId, invitation.id, invitation.albumId, invitation.albumTitle, recipients, isResending, onResendSuccess]);

   // Handle cancel single recipient
   const handleCancelRecipient = useCallback((recipient: InvitationRecipient) => {
      setRecipientToCancel(recipient);
      setShowCancelDialog(true);
   }, []);

   const handleCancelConfirm = useCallback(async () => {
      if (!recipientToCancel || cancellingRecipient) return;

      setCancellingRecipient(recipientToCancel.recipientEmail);
      setError(null);

      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${invitation.albumId}/cancel-invite`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  email: recipientToCancel.recipientEmail,
               }),
            }
         );

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to cancel invitation');
         }

         // Remove the recipient from local state
         setLocalRecipients(prev => prev.filter(r => r.recipientEmail !== recipientToCancel.recipientEmail));
         
         setShowCancelDialog(false);
         setRecipientToCancel(null);

         // If no more recipients, hide the card
         if (localRecipients.length <= 1) {
            setIsVisible(false);
            setTimeout(() => {
               onCancelSuccess?.(invitation.batchId || invitation.id);
            }, 300);
         }
      } catch (err) {
         console.error('Error cancelling invitation:', err);
         setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
         setShowCancelDialog(false);
         setTimeout(() => setError(null), 5000);
      } finally {
         setCancellingRecipient(null);
      }
   }, [recipientToCancel, cancellingRecipient, invitation.albumId, invitation.batchId, invitation.id, localRecipients.length, onCancelSuccess]);

   // Handle view album
   const handleViewAlbum = useCallback(() => {
      if (onViewAlbum) {
         onViewAlbum(invitation.albumId);
      } else {
         navigate(`/albums/${invitation.albumId}`);
      }
   }, [invitation.albumId, navigate, onViewAlbum]);

   return (
      <Fade in={isVisible} timeout={300}>
         <Card
            sx={{
               borderRadius: 2,
               boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
               border: '1px solid #e5e7eb',
               borderLeft: '4px solid #3B82F6',
               overflow: 'visible',
               background: isExpired
                  ? 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)'
                  : 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #f0f9ff 100%)',
               transition: 'all 0.2s ease-in-out',
               opacity: isExpired ? 0.75 : 1,
               '&:hover': {
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15)',
                  transform: 'translateY(-1px)',
               },
            }}
         >
            <CardContent sx={{ p: 2.5 }}>
               {/* Header */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'flex-start',
                     mb: 2,
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <MailIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {invitation.isReminder ? 'Reminder Invite' : 'Invitations Sent'}
                     </Typography>
                  </Box>

                  {expirationText && (
                     <Chip
                        icon={isExpired ? <WarningIcon /> : <ScheduleIcon />}
                        label={expirationText}
                        size="small"
                        sx={{
                           height: 24,
                           fontSize: '0.75rem',
                           fontWeight: 500,
                           backgroundColor: isExpired ? '#fef2f2' : isUrgent ? '#fffbeb' : '#f3f4f6',
                           color: isExpired ? '#dc2626' : isUrgent ? '#f59e0b' : '#6b7280',
                        }}
                     />
                  )}
               </Box>

               {/* Album Information */}
               <Box
                  sx={{
                     display: 'flex',
                     gap: 2,
                     mb: 2,
                     p: 1.5,
                     backgroundColor: '#ffffff',
                     borderRadius: 1.5,
                     border: '1px solid #e5e7eb',
                  }}
               >
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        overflow: 'hidden',
                        flexShrink: 0,
                        backgroundColor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                     }}
                  >
                     {invitation.albumCoverUrl ? (
                        <Box
                           component="img"
                           src={invitation.albumCoverUrl}
                           alt={invitation.albumTitle}
                           sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                     ) : (
                        <PhotoLibraryIcon sx={{ fontSize: 28, color: '#9ca3af' }} />
                     )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                        {invitation.albumTitle}
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <PhotoLibraryIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                           <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {invitation.albumPhotoCount} photos
                           </Typography>
                        </Box>
                        <Chip
                           icon={invitation.albumIsPublic ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                           label={invitation.albumIsPublic ? 'Public' : 'Private'}
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: invitation.albumIsPublic ? '#ecfdf5' : '#f3f4f6',
                              color: invitation.albumIsPublic ? '#10b981' : '#6b7280',
                              border: 'none',
                           }}
                        />
                     </Box>
                  </Box>
               </Box>

               {/* Recipients Summary */}
               <Box
                  sx={{
                     p: 1.5,
                     mb: 2,
                     backgroundColor: '#f9fafb',
                     borderRadius: 1.5,
                     border: '1px solid #e5e7eb',
                     overflow: 'visible',
                  }}
               >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                           {totalCount} Recipient{totalCount !== 1 ? 's' : ''}
                        </Typography>
                     </Box>
                  </Box>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                     {pendingCount > 0 && (
                        <Chip
                           icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                           label={`${pendingCount} Pending`}
                           size="small"
                           sx={{
                              height: 24,
                              backgroundColor: '#eff6ff',
                              color: '#3b82f6',
                              fontWeight: 500,
                           }}
                        />
                     )}
                     {acceptedCount > 0 && (
                        <Chip
                           icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                           label={`${acceptedCount} Accepted`}
                           size="small"
                           sx={{
                              height: 24,
                              backgroundColor: '#ecfdf5',
                              color: '#10b981',
                              fontWeight: 500,
                           }}
                        />
                     )}
                     {declinedCount > 0 && (
                        <Chip
                           icon={<CancelIcon sx={{ fontSize: 14 }} />}
                           label={`${declinedCount} Declined`}
                           size="small"
                           sx={{
                              height: 24,
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              fontWeight: 500,
                           }}
                        />
                     )}
                  </Box>

                  {/* Horizontal Scrollable Recipients List */}
                  <Box
                     sx={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                           height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                           backgroundColor: '#f1f5f9',
                           borderRadius: 3,
                        },
                        '&::-webkit-scrollbar-thumb': {
                           backgroundColor: '#cbd5e1',
                           borderRadius: 3,
                           '&:hover': {
                              backgroundColor: '#94a3b8',
                           },
                        },
                     }}
                  >
                     <Box 
                        sx={{ 
                           display: 'inline-flex',
                           flexDirection: 'row',
                           flexWrap: 'nowrap',
                           gap: 1.5, 
                           minWidth: 'min-content',
                           width: 'max-content',
                        }}
                     >
                        {recipients.map((recipient, index) => {
                           const statusConfig = getStatusColor(recipient.status);
                           const isCancelling = cancellingRecipient === recipient.recipientEmail;
                           return (
                              <Box
                                 key={index}
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 110,
                                    minWidth: 110,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: alpha(statusConfig.color, 0.05),
                                    border: `1px solid ${alpha(statusConfig.color, 0.2)}`,
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: alpha(statusConfig.color, 0.1),
                                       transform: 'translateY(-2px)',
                                       boxShadow: `0 4px 12px ${alpha(statusConfig.color, 0.15)}`,
                                    },
                                 }}
                              >
                                 {/* Cancel button for pending invitations */}
                                 {recipient.status === 'pending' && !isExpired && (
                                    <Tooltip title="Cancel invitation">
                                       <IconButton
                                          size="small"
                                          onClick={() => handleCancelRecipient(recipient)}
                                          disabled={isCancelling}
                                          sx={{
                                             position: 'absolute',
                                             top: 2,
                                             right: 2,
                                             width: 20,
                                             height: 20,
                                             backgroundColor: '#fef2f2',
                                             color: '#dc2626',
                                             '&:hover': {
                                                backgroundColor: '#fee2e2',
                                             },
                                          }}
                                       >
                                          {isCancelling ? (
                                             <CircularProgress size={12} color="inherit" />
                                          ) : (
                                             <CloseIcon sx={{ fontSize: 14 }} />
                                          )}
                                       </IconButton>
                                    </Tooltip>
                                 )}

                                 {/* Avatar with status border */}
                                 <Box sx={{ position: 'relative', mb: 1 }}>
                                    <Avatar
                                       src={recipient.recipientPhoto}
                                       sx={{
                                          width: 44,
                                          height: 44,
                                          fontSize: '1rem',
                                          fontWeight: 600,
                                          border: `3px solid ${statusConfig.color}`,
                                          backgroundColor: alpha(statusConfig.color, 0.2),
                                          color: statusConfig.color,
                                       }}
                                    >
                                       {recipient.recipientName?.[0]?.toUpperCase() || 'U'}
                                    </Avatar>
                                    {/* Status indicator dot */}
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          right: 0,
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: statusConfig.color,
                                          border: '2px solid #fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                       }}
                                    >
                                       {recipient.status === 'accepted' && (
                                          <CheckCircleIcon sx={{ fontSize: 8, color: '#fff' }} />
                                       )}
                                       {recipient.status === 'declined' && (
                                          <CancelIcon sx={{ fontSize: 8, color: '#fff' }} />
                                       )}
                                       {recipient.status === 'pending' && (
                                          <ScheduleIcon sx={{ fontSize: 8, color: '#fff' }} />
                                       )}
                                    </Box>
                                 </Box>

                                 {/* Name */}
                                 <Typography
                                    variant="body2"
                                    sx={{
                                       fontWeight: 600,
                                       color: '#374151',
                                       textAlign: 'center',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                       width: '100%',
                                       lineHeight: 1.2,
                                       fontSize: '0.8rem',
                                    }}
                                 >
                                    {recipient.recipientName || 'Unknown'}
                                 </Typography>

                                 {/* Email */}
                                 <Tooltip title={recipient.recipientEmail} arrow>
                                    <Typography
                                       variant="caption"
                                       sx={{
                                          color: '#6b7280',
                                          textAlign: 'center',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          fontSize: '0.65rem',
                                          lineHeight: 1.3,
                                       }}
                                    >
                                       {recipient.recipientEmail}
                                    </Typography>
                                 </Tooltip>

                                 {/* Status Chip */}
                                 <Chip
                                    label={recipient.status.charAt(0).toUpperCase() + recipient.status.slice(1)}
                                    size="small"
                                    sx={{
                                       mt: 0.75,
                                       height: 18,
                                       fontSize: '0.6rem',
                                       fontWeight: 600,
                                       backgroundColor: statusConfig.bg,
                                       color: statusConfig.color,
                                       '& .MuiChip-label': {
                                          px: 0.75,
                                       },
                                    }}
                                 />
                              </Box>
                           );
                        })}
                     </Box>
                  </Box>
               </Box>

               {/* Timing and Expiration */}
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                     Sent {formatRelativeTime(invitation.lastResentAt || invitation.sentAt)}
                  </Typography>
                  </Box>
                  
                  {/* Expiration Status */}
                  {expirationText && (
                     <Chip
                        icon={isExpired ? <WarningIcon sx={{ fontSize: 14 }} /> : <ScheduleIcon sx={{ fontSize: 14 }} />}
                        label={expirationText}
                        size="small"
                        sx={{
                           height: 22,
                           fontSize: '0.7rem',
                           fontWeight: 600,
                           backgroundColor: isExpired ? '#fef2f2' : isUrgent ? '#fffbeb' : '#f3f4f6',
                           color: isExpired ? '#dc2626' : isUrgent ? '#d97706' : '#6b7280',
                           border: 'none',
                        }}
                     />
                  )}
               </Box>

               {/* Success/Error Messages */}
               {showResendSuccess && (
                  <Fade in={showResendSuccess}>
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1,
                           p: 1.5,
                           mb: 2,
                           backgroundColor: '#ecfdf5',
                           borderRadius: 1,
                           border: '1px solid #a7f3d0',
                        }}
                     >
                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 500 }}>
                           Invitations resent!
                        </Typography>
                     </Box>
                  </Fade>
               )}

               {error && (
                  <Fade in={!!error}>
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1,
                           p: 1.5,
                           mb: 2,
                           backgroundColor: '#fef2f2',
                           borderRadius: 1,
                           border: '1px solid #fecaca',
                        }}
                     >
                        <WarningIcon sx={{ color: '#dc2626', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 500 }}>
                           {error}
                        </Typography>
                     </Box>
                  </Fade>
               )}

               {/* Action Buttons */}
               <Stack direction="row" spacing={1.5}>
                  <Button
                     variant="contained"
                     size="small"
                     startIcon={isResending ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                     onClick={handleResend}
                     disabled={isResending || (pendingCount === 0 && declinedCount === 0 && !isExpired)}
                     sx={{
                        flex: 1,
                        backgroundColor: isExpired ? '#10b981' : '#3b82f6',
                        color: '#ffffff',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 1.5,
                        py: 1,
                        '&:hover': { backgroundColor: isExpired ? '#059669' : '#2563eb' },
                        '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                     }}
                  >
                     {isResending ? (isExpired ? 'Reactivating...' : 'Resending...') : (isExpired ? 'Reactivate & Resend' : 'Resend All')}
                  </Button>

                  <Tooltip title="View Album">
                     <IconButton size="small" onClick={handleViewAlbum} sx={{ color: '#6b7280' }}>
                        <OpenInNewIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
               </Stack>
            </CardContent>

            {/* Cancel Single Recipient Dialog */}
            <Dialog open={showCancelDialog} onClose={() => !cancellingRecipient && setShowCancelDialog(false)}>
               <DialogTitle>Cancel Invitation</DialogTitle>
               <DialogContent>
                  <DialogContentText>
                     Are you sure you want to cancel the invitation for{' '}
                     <strong>{recipientToCancel?.recipientName || recipientToCancel?.recipientEmail}</strong>?
                  </DialogContentText>
               </DialogContent>
               <DialogActions>
                  <Button onClick={() => setShowCancelDialog(false)} disabled={!!cancellingRecipient}>
                     Keep
                  </Button>
                  <Button
                     onClick={handleCancelConfirm}
                     disabled={!!cancellingRecipient}
                     sx={{ color: '#dc2626' }}
                  >
                     {cancellingRecipient ? 'Cancelling...' : 'Yes, Cancel'}
                  </Button>
               </DialogActions>
            </Dialog>
         </Card>
      </Fade>
   );
});

// Display name for debugging
AlbumInvitationSentCard.displayName = 'AlbumInvitationSentCard';

export default AlbumInvitationSentCard;
